import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { formatDateTime, timeAgo } from '../utils/dateFormat';
import { useWS } from '../hooks/useWebSocket';

/* ── Badge helpers ─────────────────────────────────────── */

function DeviceStatusBadge({ status }) {
  const map = {
    online: 'bg-emerald-50 text-emerald-700',
    offline: 'bg-red-50 text-red-700',
    vacation: 'bg-blue-50 text-blue-700',
    idle: 'bg-secondary-100 text-secondary-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.idle}`}>
      {status}
    </span>
  );
}

function SeverityBadge({ severity }) {
  const map = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-amber-100 text-amber-800',
    low: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[severity] || map.medium}`}>
      {severity}
    </span>
  );
}

function AlertStatusBadge({ status }) {
  const map = {
    pending: 'bg-red-50 text-red-700',
    acknowledged: 'bg-amber-50 text-amber-700',
    escalated: 'bg-purple-50 text-purple-700',
    resolved: 'bg-emerald-50 text-emerald-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || ''}`}>
      {status}
    </span>
  );
}

/* ── Stat card ─────────────────────────────────────────── */

function StatCard({ label, value, color = 'emerald', icon, to }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    slate: 'bg-secondary-100 text-secondary-600',
  };
  const inner = (
    <>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
          </svg>
        </div>
        <span className="text-sm text-secondary-500">{label}</span>
      </div>
      <p className="text-2xl sm:text-3xl font-bold text-secondary-900">{value ?? '--'}</p>
    </>
  );
  const cls =
    'block bg-white rounded-xl shadow-card p-4 sm:p-5 hover:shadow-card-hover active:scale-[0.98] transition min-h-[100px] focus:outline-none focus:ring-2 focus:ring-emerald-400';
  if (to) {
    return (
      <Link to={to} className={cls} aria-label={`${label}: ${value ?? '--'}`}>
        {inner}
      </Link>
    );
  }
  return <div className={cls}>{inner}</div>;
}

/* ── Device card ───────────────────────────────────────── */

function DeviceCard({ device }) {
  const target = device.id ? `/devices/${device.id}` : '/devices';
  return (
    <Link
      to={target}
      className="block bg-white rounded-xl shadow-card p-4 sm:p-5 hover:shadow-card-hover active:scale-[0.98] transition focus:outline-none focus:ring-2 focus:ring-emerald-400"
      aria-label={`Device ${device.device_id}`}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-secondary-900 truncate">
            {device.patient_name || 'Unassigned'}
          </p>
          <p className="text-xs text-secondary-400 mt-0.5 truncate">{device.device_id}</p>
        </div>
        <DeviceStatusBadge status={device.vacation_mode ? 'vacation' : device.status} />
      </div>

      <div className="flex items-center justify-end text-xs text-secondary-500">
        <span title={formatDateTime(device.last_seen_at)}>
          {timeAgo(device.last_seen_at)}
        </span>
      </div>

      {device.pending_alerts > 0 && (
        <div className="mt-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 rounded-lg">
          <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs font-medium text-red-700">
            {device.pending_alerts} pending alert{device.pending_alerts > 1 ? 's' : ''}
          </span>
        </div>
      )}
    </Link>
  );
}

/* ── Alert row ─────────────────────────────────────────── */

function AlertRow({ alert, onAcknowledge, onEscalate }) {
  const [showEscalate, setShowEscalate] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const handleAck = async () => {
    setBusy(true);
    await onAcknowledge(alert.id);
    setBusy(false);
  };

  const handleEscalate = async () => {
    if (!notes.trim()) return;
    setBusy(true);
    await onEscalate(alert.id, notes.trim());
    setShowEscalate(false);
    setNotes('');
    setBusy(false);
  };

  return (
    <div className="px-5 py-4 hover:bg-secondary-50 transition">
      {/* Top row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-secondary-800">
            {alert.alert_type.replace(/_/g, ' ')}
          </span>
          <SeverityBadge severity={alert.severity} />
          <AlertStatusBadge status={alert.status} />
        </div>
        <span className="text-xs text-secondary-400 whitespace-nowrap" title={formatDateTime(alert.created_at)}>
          {timeAgo(alert.created_at)}
        </span>
      </div>

      {/* Detail */}
      <div className="mt-1.5 flex items-center gap-2 text-xs text-secondary-500">
        <span className="font-medium text-emerald-700">{alert.patient_name || '--'}</span>
        <span>&middot;</span>
        <span>{alert.device_id}</span>
      </div>
      {alert.message && (
        <p className="mt-1 text-sm text-secondary-600">{alert.message}</p>
      )}

      {/* Actions */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {alert.status === 'pending' && (
          <button
            onClick={handleAck}
            disabled={busy}
            className="px-3 py-2 min-h-[36px] text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 active:bg-amber-200 transition disabled:opacity-50"
          >
            Acknowledge
          </button>
        )}
        <button
          onClick={() => setShowEscalate((v) => !v)}
          className="px-3 py-2 min-h-[36px] text-xs font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 active:bg-red-200 transition"
        >
          {showEscalate ? 'Cancel' : 'Escalate'}
        </button>
      </div>

      {/* Escalate form */}
      {showEscalate && (
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            placeholder="Add escalation notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="flex-1 px-3 py-1.5 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <button
            onClick={handleEscalate}
            disabled={busy || !notes.trim()}
            className="px-4 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
          >
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main component ────────────────────────────────────── */

export default function MonitorDashboard() {
  const [stats, setStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertFilter, setAlertFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Fetch all data */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, devicesRes, alertsRes] = await Promise.all([
        api.get('/monitor/stats'),
        api.get('/monitor/devices'),
        api.get('/monitor/alerts', { params: alertFilter ? { status: alertFilter } : {} }),
      ]);
      setStats(statsRes.data.stats);
      setDevices(devicesRes.data.data);
      setAlerts(alertsRes.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  }, [alertFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* Auto-refresh: WebSocket-driven with 60s fallback.
     Any device/alert event triggers an immediate refetch so the dashboard
     updates in real time. The 60s poll remains as a safety net in case the
     WS connection drops silently. */
  useWS([
    'device_status_change', 'telemetry_update', 'new_alert',
    'alert_acknowledged', 'alert_resolved',
    'device:vacation_mode_changed', 'device:config_applied',
    'checkin:slot_completed', 'checkin:slot_missed',
    'button_press',
  ], useCallback(() => fetchData(), [fetchData]));

  useEffect(() => {
    const timer = setInterval(fetchData, 60000);
    return () => clearInterval(timer);
  }, [fetchData]);

  /* Alert actions */
  const handleAcknowledge = async (alertId) => {
    try {
      await api.post(`/monitor/alerts/${alertId}/acknowledge`);
      fetchData();
    } catch {}
  };

  const handleEscalate = async (alertId, notes) => {
    try {
      await api.post(`/monitor/alerts/${alertId}/escalate`, { notes });
      fetchData();
    } catch {}
  };

  /* Loading / error states */
  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64 text-secondary-400">
        <svg className="w-5 h-5 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Loading...
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-red-500">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">Monitor Dashboard</h1>
          <p className="text-secondary-500 text-sm mt-1">Real-time device and alert monitoring</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="self-start sm:self-auto px-4 py-2.5 min-h-[44px] text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 active:bg-emerald-800 transition disabled:opacity-50 flex items-center gap-2"
        >
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Stats bar — clickable */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          <StatCard
            to="/devices"
            label="Total Devices"
            value={stats.total_devices}
            color="slate"
            icon="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
          <StatCard
            to="/devices"
            label="Online"
            value={stats.online_devices}
            color="emerald"
            icon="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728M9.172 14.828a4 4 0 010-5.656m5.656 0a4 4 0 010 5.656M12 12h.01"
          />
          <StatCard
            to="/devices"
            label="Offline"
            value={stats.offline_devices}
            color="red"
            icon="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728m2.829 9.9a5 5 0 010-7.072m7.072 0a5 5 0 010 7.072M13 12a1 1 0 11-2 0 1 1 0 012 0z"
          />
          <StatCard
            to="/devices"
            label="Vacation"
            value={stats.vacation_devices}
            color="blue"
            icon="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
          <StatCard
            to="/alerts"
            label="Pending Alerts"
            value={stats.pending}
            color="amber"
            icon="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
          <StatCard
            to="/alerts"
            label="Critical"
            value={stats.critical_pending}
            color="red"
            icon="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </div>
      )}

      {/* Device cards */}
      <div>
        <h2 className="text-lg font-semibold text-secondary-900 mb-3">Devices</h2>
        {devices.length === 0 ? (
          <div className="bg-white rounded-xl shadow-card p-8 text-center text-secondary-400 text-sm">
            No devices found
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {devices.map((d) => (
              <DeviceCard key={d.device_id} device={d} />
            ))}
          </div>
        )}
      </div>

      {/* Alerts section */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold text-secondary-900">Alerts</h2>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'pending', label: 'Pending' },
              { value: 'acknowledged', label: 'Acknowledged' },
              { value: '', label: 'All' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setAlertFilter(opt.value)}
                className={`px-3 py-2 min-h-[40px] text-sm rounded-lg border transition ${
                  alertFilter === opt.value
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                    : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50 active:bg-secondary-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-card overflow-hidden">
          {alerts.length === 0 ? (
            <div className="px-5 py-8 text-center text-secondary-400 text-sm">
              No {alertFilter || ''} alerts
            </div>
          ) : (
            <div className="divide-y divide-secondary-100">
              {alerts.map((a) => (
                <AlertRow
                  key={a.id}
                  alert={a}
                  onAcknowledge={handleAcknowledge}
                  onEscalate={handleEscalate}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
